import React from 'react';
import { appDayMonth } from '../../../../utils/appDateFormat';

// Simple inline stub for Icon component
const Icon = ({ name, size = 16, className = '' }) => {
  const sizeStr = size + 'px';
  return <span className={className} style={{ display: 'inline-flex', width: sizeStr, height: sizeStr }} />;
};

const ProjectListView = ({ projects, needsAttention, onView, onEdit, onDelete }) => {
  const getHealthConfig = (health) => {
    switch (health) {
      case 'On Track':
        return { color: 'text-green-600', bg: 'bg-green-50', icon: 'CheckCircle' };
      case 'At Risk':
        return { color: 'text-amber-600', bg: 'bg-amber-50', icon: 'AlertCircle' };
      case 'Blocked':
        return { color: 'text-red-600', bg: 'bg-red-50', icon: 'XCircle' };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-50', icon: 'Circle' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'Completed':
        return 'bg-gray-100 text-gray-800';
      case 'On Hold':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return appDayMonth(dateString);
  };

  const isOverdue = (project) => {
    return new Date(project?.dateRange?.end) < new Date() && project?.status === 'Active';
  };

  const progressPercentage = (project) => {
    return Math.round((project?.completedTasks / project?.taskCount) * 100) || 0;
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Health
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tasks
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timeline
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projects?.map((project) => {
              const healthConfig = getHealthConfig(project?.health);
              const progress = progressPercentage(project);
              const overdue = isOverdue(project);
              const attention = needsAttention(project);

              return (
                <tr
                  key={project?.id}
                  className={`hover:bg-gray-50 transition-colors ${
                    attention ? 'bg-amber-50/30 border-l-4 border-l-amber-500' : ''
                  }`}
                >
                  {/* Project Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {attention && (
                        <Icon name="AlertTriangle" size={16} className="text-amber-500 mr-2 flex-shrink-0" />
                      )}
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{project?.name}</div>
                        <div className="text-xs text-gray-500">{project?.id}</div>
                      </div>
                    </div>
                  </td>

                  {/* Health */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center px-2.5 py-1 rounded-full ${healthConfig?.bg}`}>
                      <Icon name={healthConfig?.icon} size={14} className={`${healthConfig?.color} mr-1`} />
                      <span className={`text-xs font-medium ${healthConfig?.color}`}>
                        {project?.health}
                      </span>
                    </div>
                  </td>

                  {/* Progress */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${
                            project?.health === 'On Track' ? 'bg-green-500' :
                            project?.health === 'At Risk'? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700">{progress}%</span>
                    </div>
                  </td>

                  {/* Tasks */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <span>{project?.completedTasks}/{project?.taskCount}</span>
                    </div>
                  </td>

                  {/* Owner */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-xs">
                        {project?.owner?.initials}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{project?.owner?.name}</div>
                        <div className="text-xs text-gray-500 flex items-center">
                          <Icon name="Users" size={10} className="mr-1" />
                          {project?.teamMembers}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Timeline */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div>{formatDate(project?.dateRange?.start)} - {formatDate(project?.dateRange?.end)}</div>
                      {overdue && (
                        <div className="flex items-center text-red-600 text-xs font-semibold mt-1">
                          <Icon name="AlertCircle" size={10} className="mr-1" />
                          Overdue
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(project?.status)}`}>
                      {project?.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onView(project)}
                        className="text-primary hover:text-primary/80 p-1.5 hover:bg-primary/10 rounded transition-colors"
                        title="View"
                      >
                        <Icon name="Eye" size={16} />
                      </button>
                      <button
                        onClick={() => onEdit(project)}
                        className="text-gray-600 hover:text-gray-900 p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Icon name="Edit" size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(project)}
                        className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded transition-colors"
                        title="Archive"
                      >
                        <Icon name="Archive" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectListView;
